import { motion } from "framer-motion";
import { CTAButton } from "./CTAButton";
import { ProductFrame } from "./ProductFrame";
import { usePrefersReducedMotion } from "@/hooks/usePrefersReducedMotion";

const EASE = [0.76, 0, 0.24, 1];

const WhatsAppIcon = ({ className = "" }) => (
  <svg
    viewBox="0 0 24 24"
    className={className}
    fill="currentColor"
    aria-hidden="true"
  >
    <path d="M12.04 2c-5.46 0-9.91 4.45-9.91 9.91 0 1.75.46 3.45 1.32 4.95L2 22l5.25-1.38a9.87 9.87 0 0 0 4.79 1.22h.01c5.46 0 9.91-4.45 9.91-9.91S17.5 2 12.04 2Zm0 18.15h-.01a8.2 8.2 0 0 1-4.18-1.14l-.3-.18-3.11.82.83-3.04-.2-.31a8.2 8.2 0 0 1-1.26-4.39c0-4.54 3.7-8.23 8.24-8.23 2.2 0 4.27.86 5.82 2.41a8.18 8.18 0 0 1 2.41 5.83c0 4.54-3.7 8.23-8.24 8.23Zm4.52-6.16c-.25-.12-1.47-.72-1.69-.81-.23-.08-.39-.12-.56.12-.16.25-.64.81-.79.98-.14.16-.29.18-.54.06-.25-.12-1.05-.39-1.99-1.23-.74-.66-1.23-1.47-1.38-1.72-.14-.25-.02-.38.11-.5.11-.11.25-.29.37-.43.13-.14.17-.25.25-.41.08-.16.04-.31-.02-.43-.06-.12-.56-1.34-.76-1.84-.2-.48-.41-.42-.56-.43l-.48-.01c-.16 0-.43.06-.66.31-.23.25-.86.85-.86 2.07 0 1.22.89 2.4 1.01 2.56.12.16 1.75 2.67 4.25 3.74.59.26 1.06.41 1.42.52.6.19 1.14.16 1.57.1.48-.07 1.47-.6 1.68-1.18.21-.58.21-1.07.14-1.18-.06-.11-.22-.17-.47-.29Z" />
  </svg>
);

const KineticLine = ({ children, delay = 0, reduced }) => (
  <motion.span
    className="block"
    initial={{ opacity: 0, y: reduced ? 0 : 22 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: reduced ? 0.5 : 0.8, ease: EASE, delay }}
  >
    {children}
  </motion.span>
);

export const Hero = () => {
  const reduced = usePrefersReducedMotion();

  return (
    <section
      id="hero"
      className="relative pt-28 md:pt-36 pb-12 md:pb-16 overflow-hidden"
      data-testid="hero-section"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-8 items-center">
        <div className="lg:col-span-6">
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: "easeOut" }}
            className="inline-flex items-center gap-2 rounded-full border border-[#171717]/10 bg-white/85 pl-2.5 pr-4 py-1.5 mb-7"
            data-testid="hero-whatsapp-badge"
          >
            <span className="grid place-items-center h-6 w-6 rounded-full bg-[#25D366] text-white">
              <WhatsAppIcon className="h-3.5 w-3.5" />
            </span>
            <span className="text-sm font-medium text-[#171717]/75">
              Lives inside WhatsApp — no new app to download
            </span>
          </motion.div>

          <h1 className="font-heading text-5xl md:text-6xl lg:text-[4.4rem] leading-[1.05] tracking-tight font-medium text-[#171717]">
            <KineticLine delay={0.05} reduced={reduced}>
              Don&rsquo;t just track
            </KineticLine>
            <KineticLine delay={0.18} reduced={reduced}>
              your health.
            </KineticLine>
            <KineticLine delay={0.31} reduced={reduced}>
              <span className="text-[#176B4D]">Understand it.</span>
            </KineticLine>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.6, ease: "easeOut" }}
            className="mt-7 md:mt-8 text-lg md:text-xl leading-relaxed text-[#171717]/70 max-w-[480px]"
            data-testid="hero-subcopy"
          >
            Quantify brings your food, workouts, weight and habits together
            — all inside a simple WhatsApp chat — then turns them into
            personalised insights that help you make better decisions every
            day.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.75, ease: "easeOut" }}
            className="mt-10"
          >
            <CTAButton
              eventName="hero_cta_click"
              size="lg"
              testId="hero-cta-waitlist"
            />
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.35, ease: EASE }}
          className="lg:col-span-6"
        >
          <ProductFrame eventId="hero" priority />
        </motion.div>
      </div>
    </section>
  );
};
